'use client';

import React, { useEffect, useRef, useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';

export default function HeroSection() {
  const line1Ref = useRef<HTMLSpanElement>(null);
  const line2Ref = useRef<HTMLSpanElement>(null);
  const line3Ref = useRef<HTMLSpanElement>(null);
  const subRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const els = [
    { el: line1Ref.current, delay: 100 },
    { el: line2Ref.current, delay: 280 },
    { el: line3Ref.current, delay: 460 },
    { el: subRef.current, delay: 680 },
    { el: ctaRef.current, delay: 820 },
    { el: searchRef.current, delay: 1000 }];


    els.forEach(({ el, delay }) => {
      if (!el) return;
      setTimeout(() => {
        el.style.transform = 'translateY(0)';
        el.style.opacity = '1';
        el.style.filter = 'blur(0)';
      }, delay);
    });
  }, []);

  const lineStyle: React.CSSProperties = {
    transform: 'translateY(40px)',
    opacity: '0',
    filter: 'blur(6px)',
    transition: 'transform 0.9s cubic-bezier(0.16,1,0.3,1), opacity 0.9s cubic-bezier(0.16,1,0.3,1), filter 0.9s cubic-bezier(0.16,1,0.3,1)',
    display: 'block'
  };

  return (
    <section className="relative min-h-screen flex flex-col justify-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <AppImage
          src="https://images.unsplash.com/photo-1734966192133-6704164dc58c"
          alt="Modern apartment buildings in Kathmandu Nepal with urban residential architecture and city skyline at dusk"
          fill
          priority
          className="object-cover"
          sizes="100vw" />
        
        {/* Multi-layer overlay for depth */}
        <div className="absolute inset-0 bg-gradient-to-b from-primary/80 via-primary/60 to-primary/85" />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground/30 via-transparent to-transparent" />
      </div>

      {/* Atmospheric depth blobs */}
      <div className="absolute top-1/4 right-1/4 w-96 h-96 opacity-20 pointer-events-none z-10"
      style={{ background: 'radial-gradient(ellipse, rgba(200,150,90,0.4) 0%, transparent 70%)', filter: 'blur(80px)' }} />
      <div className="absolute bottom-1/3 left-1/5 w-64 h-64 opacity-15 pointer-events-none z-10"
      style={{ background: 'radial-gradient(ellipse, rgba(45,106,79,0.6) 0%, transparent 70%)', filter: 'blur(60px)' }} />

      {/* Content */}
      <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-8 w-full">
        <div className="max-w-4xl">
          {/* Eyebrow */}
          <div
            ref={subRef as React.RefObject<HTMLDivElement>}
            style={{ ...lineStyle, transitionDelay: '0ms' }}
            className="flex items-center gap-3 mb-6">
            
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm">
              <div className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
              <span className="text-[11px] font-mono tracking-widest uppercase text-white/80">
                Kathmandu, Nepal
              </span>
            </div>
            <div className="w-8 h-px bg-accent/60" />
            <span className="text-[11px] font-mono tracking-widest uppercase text-white/60">
              Property Management
            </span>
          </div>

          {/* Headline */}
          <h1 className="mb-6 text-white">
            <span
              ref={line1Ref}
              style={lineStyle}
              className="text-hero-xl font-bold block leading-tight">
              
              Professional
            </span>
            <span
              ref={line2Ref}
              style={{ ...lineStyle, transitionDelay: '0ms' }}
              className="text-hero-xl font-bold block leading-tight">
              
              Property Management
            </span>
            <span
              ref={line3Ref}
              style={{ ...lineStyle, transitionDelay: '0ms' }}
              className="text-hero-xl font-extrabold block leading-tight text-gradient-accent">
              
              You Can Trust.
            </span>
          </h1>

          {/* Subheadline */}
          <div
            ref={ctaRef}
            style={lineStyle}
            className="mb-8">
            
            <p className="text-base sm:text-lg text-white/75 max-w-2xl leading-relaxed">
              From finding reliable tenants and collecting rent to property maintenance and financial reporting,
              Empire Venture Nepal helps you manage your investment with confidence.
            </p>
          </div>

          {/* CTA Buttons */}
          <div
            ref={searchRef}
            style={lineStyle}
            className="flex flex-wrap gap-3 mb-12">
            
            <Link
              href="/properties"
              className="flex items-center gap-2 px-6 py-3.5 bg-accent text-foreground font-semibold rounded-lg hover:bg-accent/90 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 text-sm sm:text-base">
              
              <Icon name="MagnifyingGlassIcon" size={18} />
              Find a Rental
            </Link>
            <Link
              href="/owner-services"
              className="flex items-center gap-2 px-6 py-3.5 bg-white/15 text-white border border-white/30 font-semibold rounded-lg hover:bg-white/25 transition-all duration-300 backdrop-blur-sm text-sm sm:text-base">
              
              <Icon name="BuildingOffice2Icon" size={18} />
              Owner Services
            </Link>
          </div>
        </div>
      </div>

      {/* Search Panel — overlapping bottom of hero */}
      <div className="relative z-30 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pb-0">
        <SearchPanel />
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-1 opacity-50">
        <span className="text-[10px] font-mono tracking-widest uppercase text-white/60">Scroll</span>
        <div className="w-px h-10 bg-gradient-to-b from-white/40 to-transparent" />
      </div>
    </section>);

}

function SearchPanel() {
  const [priceMin, setPriceMin] = React.useState('');
  const [priceMax, setPriceMax] = React.useState('');

  return (
    <div className="search-glass rounded-xl sm:rounded-2xl p-5 sm:p-6 -mb-8 sm:-mb-12 relative">
      <div className="flex items-center gap-2 mb-4">
        <Icon name="MagnifyingGlassIcon" size={16} className="text-primary" />
        <span className="text-sm font-semibold text-foreground">Find Your Ideal Property</span>
        <div className="ml-auto flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-muted">
          <div className="w-1.5 h-1.5 rounded-full bg-accent" />
          <span className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider">Live Search</span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {/* Location */}
        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">Location</label>
          <select className="select-field px-3 py-2.5 text-sm">
            <option value="">All Locations</option>
            <option>New Baneshwor</option>
            <option>Koteswor</option>
            <option>New Road</option>
            <option>Lazimpat</option>
            <option>Khichapokhari</option>
            <option>Putalisadak</option>
            <option>Hattisar</option>
            <option>Naxal</option>
          </select>
        </div>

        {/* Property Type */}
        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">Property Type</label>
          <select className="select-field px-3 py-2.5 text-sm">
            <option value="">All Types</option>
            <option>Apartment / Flat</option>
            <option>House</option>
            <option>Commercial Space</option>
            <option>Office Space</option>
            <option>Other</option>
          </select>
        </div>

        {/* Bedrooms */}
        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">Bedrooms</label>
          <select className="select-field px-3 py-2.5 text-sm">
            <option value="">Any Bedrooms</option>
            <option>Studio</option>
            <option>1 Bedroom</option>
            <option>2 Bedrooms</option>
            <option>3 Bedrooms</option>
            <option>4+ Bedrooms</option>
          </select>
        </div>

        {/* Price Range */}
        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">Monthly Rent (NPR)</label>
          <div className="flex gap-1.5">
            <input
              type="number"
              placeholder="Min"
              value={priceMin}
              onChange={(e) => setPriceMin(e.target.value)}
              className="input-field px-2.5 py-2.5 text-sm w-full" />
            
            <input
              type="number"
              placeholder="Max"
              value={priceMax}
              onChange={(e) => setPriceMax(e.target.value)}
              className="input-field px-2.5 py-2.5 text-sm w-full" />
            
          </div>
        </div>

        {/* Search Button */}
        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-medium text-transparent uppercase tracking-wider select-none">Search</label>
          <Link
            href="/properties"
            className="btn-primary px-4 py-2.5 text-sm flex items-center justify-center gap-2 h-full">
            
            <Icon name="MagnifyingGlassIcon" size={16} />
            Search Properties
          </Link>
        </div>
      </div>
    </div>);

}